import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { format, parse, isValid } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import IcomoonReact from 'icomoon-react';

import SegmentedControl from '../../../../shared/_TBComponents/SegmentedControl';
import Text from '../../../../shared/_TBComponents/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { HISTORY_RANGE } from '../../constants';

const CHART_HEIGHT = 260;
const SERIES_COLOR = '#E8833A';
const ACHIEVEMENT_COLOR = '#E8A33A';
const TROPHY_SIZE = 14;

const RANGE_OPTIONS = [
  { id: HISTORY_RANGE.Last10, label: 'Last 10' },
  { id: HISTORY_RANGE.Ytd, label: 'YTD' },
  { id: HISTORY_RANGE.All, label: 'All' },
];

const ChartCard = styled.div`
  padding: 16px 16px 12px 0;
  background: ${(props) => props.theme.colors.surface};
  border: 1px solid ${(props) => props.theme.colors.borderLight};
  border-radius: 4px;
`;

const ChartFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0 0 24px;
`;

const EmptyRange = styled.div`
  display: flex;
  height: ${CHART_HEIGHT}px;
  align-items: center;
  justify-content: center;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const TooltipCard = styled.div`
  padding: 8px 12px;
  background: ${(props) => props.theme.colors.surfaceElevated};
  border: 1px solid ${(props) => props.theme.colors.borderLight};
  border-radius: 4px;
  box-shadow: ${(props) => props.theme.colors.shadowPanel};
  font-family: "Nunito Sans";
  font-size: 13px;
  color: ${(props) => props.theme.colors.text};
`;

const parseDate = (dateSet) => {
  const parsed = dateSet ? parse(dateSet, 'yyyy-MM-dd', new Date()) : null;
  return parsed && isValid(parsed) ? parsed : null;
};

// Adds the year only when the points cover more than one
const makeTickFormatter = (spansYears) => (dateSet) => {
  const parsed = parseDate(dateSet);
  return parsed ? format(parsed, spansYears ? 'd MMM yy' : 'd MMM') : dateSet;
};

// Every point gets a date label up to this many
const LABEL_EVERY_POINT_MAX = 12;

const formatSeconds = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

// Goal achieved points show a trophy - the shared Icon can't be used inside a chart
const renderPoint = (theme, isActive, dot) => {
  const { cx, cy, payload } = dot;
  if (cx === undefined || cy === undefined) return null;
  const key = `point-${payload.id}`;
  if (payload.achievementScope) {
    const size = isActive ? TROPHY_SIZE + 4 : TROPHY_SIZE;
    return (
      <g key={key}>
        <circle
          cx={cx}
          cy={cy}
          r={size / 2 + 4}
          fill={theme.colors.surface}
          stroke={ACHIEVEMENT_COLOR}
          strokeWidth={1.5}
        />
        <g transform={`translate(${cx - size / 2}, ${cy - size / 2})`}>
          <IcomoonReact iconSet={iconSet} icon='trophy' size={size} color={ACHIEVEMENT_COLOR} />
        </g>
      </g>
    );
  }
  return (
    <circle
      key={key}
      cx={cx}
      cy={cy}
      r={isActive ? 6 : 4}
      fill={SERIES_COLOR}
      stroke={theme.colors.surface}
      strokeWidth={2}
    />
  );
};

const HistoryTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  const parsed = parseDate(point.dateSet);
  return (
    <TooltipCard>
      <div>{parsed ? format(parsed, 'd MMMM yyyy') : point.dateSet}</div>
      <strong>{point.displayValue}</strong>
      {point.achievementScope && (
        <div>{`🏆 Goal achieved (${point.achievementScope})`}</div>
      )}
    </TooltipCard>
  );
};

HistoryTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    payload: PropTypes.shape({
      dateSet: PropTypes.string,
      displayValue: PropTypes.string,
      achievementScope: PropTypes.string,
    }),
  })),
};

HistoryTooltip.defaultProps = { active: false, payload: [] };

const MaxHistoryChart = ({
  points, goalRawValue, isTimed, unitLabel, range, onRangeChange, isLoading,
}) => {
  const theme = useTheme();

  // Oldest first so the chart reads left to right
  const chartData = useMemo(
    () => [...points].reverse().filter((point) => point.rawValue !== null),
    [points],
  );

  const axisTick = { fontSize: 12, fill: theme.colors.textSecondary };
  const formatTick = useMemo(() => {
    const years = new Set(chartData.map((point) => (point.dateSet || '').slice(0, 4)));
    return makeTickFormatter(years.size > 1);
  }, [chartData]);
  const labelEveryPoint = chartData.length <= LABEL_EVERY_POINT_MAX;
  const renderDot = useMemo(() => (dot) => renderPoint(theme, false, dot), [theme]);
  const renderActiveDot = useMemo(() => (dot) => renderPoint(theme, true, dot), [theme]);

  return (
    <ChartCard>
      {chartData.length > 0 ? (
        <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
          {/* Right margin stops the last date label being cut off */}
          <LineChart
            data={chartData}
            margin={{
              top: 8, right: 28, left: 8, bottom: 0,
            }}
          >
            <CartesianGrid vertical={false} stroke={theme.colors.borderLight} />
            <XAxis
              dataKey='dateSet'
              tickFormatter={formatTick}
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: theme.colors.borderLight }}
              interval={labelEveryPoint ? 0 : 'preserveStartEnd'}
              minTickGap={labelEveryPoint ? 0 : 24}
            />
            <YAxis
              domain={['auto', 'auto']}
              tickFormatter={isTimed ? formatSeconds : undefined}
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            <Tooltip content={<HistoryTooltip />} cursor={{ stroke: theme.colors.textTertiary }} />
            {goalRawValue !== null && (
              <ReferenceLine
                y={goalRawValue}
                stroke={theme.colors.textSecondary}
                strokeDasharray='4 4'
                label={{
                  value: 'Goal', position: 'insideTopLeft', fontSize: 12, fill: theme.colors.textSecondary,
                }}
              />
            )}
            <Line
              type='linear'
              dataKey='rawValue'
              stroke={SERIES_COLOR}
              strokeWidth={2}
              dot={renderDot}
              activeDot={renderActiveDot}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <EmptyRange>
          {!isLoading && (range === HISTORY_RANGE.Ytd ? 'No maxes recorded this year.' : 'No maxes to show.')}
        </EmptyRange>
      )}
      <ChartFooter>
        <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>{unitLabel}</Text>
        <SegmentedControl
          options={RANGE_OPTIONS}
          value={range}
          onChange={onRangeChange}
          ariaLabel='Chart date range'
        />
      </ChartFooter>
    </ChartCard>
  );
};

MaxHistoryChart.propTypes = {
  points: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    dateSet: PropTypes.string,
    rawValue: PropTypes.number,
    displayValue: PropTypes.string,
    achievementScope: PropTypes.string,
  })),
  goalRawValue: PropTypes.number,
  isTimed: PropTypes.bool,
  unitLabel: PropTypes.string,
  range: PropTypes.oneOf(Object.values(HISTORY_RANGE)).isRequired,
  onRangeChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

MaxHistoryChart.defaultProps = {
  points: [],
  goalRawValue: null,
  isTimed: false,
  unitLabel: '',
  isLoading: false,
};

export default MaxHistoryChart;
